const PWA_INSTALLED_STORAGE_KEY = 'ifl.pwaInstalled'
const PWA_BANNER_DISMISS_KEY = 'ifl.pwaBannerDismiss'

/** Set when the browser fires `appinstalled` (user completed install). */
export function markPwaInstalledOnDevice(): void {
  try {
    localStorage.setItem(PWA_INSTALLED_STORAGE_KEY, '1')
  } catch {
    /* private mode */
  }
}

/** True if this device has installed the PWA before (browser tab, not standalone). */
export function wasPwaInstalledOnDevice(): boolean {
  try {
    return localStorage.getItem(PWA_INSTALLED_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function isPwaBannerDismissedForSession(): boolean {
  try {
    return sessionStorage.getItem(PWA_BANNER_DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

export function dismissPwaBannerForSession(): void {
  try {
    sessionStorage.setItem(PWA_BANNER_DISMISS_KEY, '1')
  } catch {
    /* ignore */
  }
}

/** True when the app is running as an installed PWA (home screen / standalone). */
export function isPwaInstalled(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia('(display-mode: standalone)').matches) return true
  if (window.matchMedia('(display-mode: fullscreen)').matches) return true
  const nav = window.navigator as Navigator & { standalone?: boolean }
  if (nav.standalone) return true
  return false
}

/** Phone/tablet browser — not desktop web. */
export function isMobileBrowser(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false
  const ua = navigator.userAgent
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true
  return window.matchMedia('(max-width: 767px) and (pointer: coarse)').matches
}

export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isAppleMobile = /iPad|iPhone|iPod/.test(ua)
  const isMacTouch = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  return (isAppleMobile || isMacTouch) && !/(CriOS|FxiOS|EdgiOS)/.test(ua)
}

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function isBeforeInstallPromptEvent(event: Event): event is BeforeInstallPromptEvent {
  return 'prompt' in event && typeof (event as BeforeInstallPromptEvent).prompt === 'function'
}
